/**
 * App Component
 * Main application component with routing
 */

import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import LandingPageGuard from './components/common/LandingPageGuard';
import HomepageRouter from './components/common/HomepageRouter';
import WebsiteLoadingScreen from './components/common/WebsiteLoadingScreen';
import { AuthProvider, useAuthContext } from './contexts';
import { useAppVersionGuard } from './hooks';
import { ROUTES } from './utils/appConstants';
import Header from './components/layout/Header';
import ScrollManager from './utils/ScrollManager';
import Footer from './components/layout/Footer';
import ComplianceNotice from './components/layout/ComplianceNotice';

const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ConfirmAccountPage = lazy(() => import('./pages/ConfirmAccountPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const ComingSoonPage = lazy(() => import('./pages/ComingSoonPage'));
const Homepagina = lazy(() => import('./pages/Homepagina'));
const ContactPage = lazy(() => import('./pages/Contact'));
const LandingPageGratisVacature = lazy(() => import('./pages/LandingPageGratisVacature'));
const ZorgLandingPage = lazy(() => import('./pages/ZorgLandingPage'));
const FinanceLandingPage = lazy(() => import('./pages/FinanceLandingPage'));
const WerkgeverDashboardPage = lazy(() => import('./features/werkgever-dashboard/pages/WerkgeverDashboardPage'));
const StudentDashboardPage = lazy(() => import('./features/student-dashboard/pages/StudentDashboardPage'));
const CadeaukaartRequestPage = lazy(() => import('./features/cadeaukaart/pages/CadeaukaartRequestPage'));
const InvoiceRequestPage = lazy(() => import('./features/factuur/pages/InvoiceRequestPage'));
const PlaatsVacaturePage = lazy(() => import('./features/vacature-plaatsen/pages/PlaatsVacature'));
const VacatureSuccessPage = lazy(() => import('./features/vacature-plaatsen/pages/VacatureSuccessPage'));
const VacancyPage = lazy(() => import('./features/vacature-weergeven/components/VacancyPage'));
const SearchResults = lazy(() => import('./features/vacature-weergeven/components/SearchResults'));
const ChatPage = lazy(() => import('./features/chat/pages/ChatPage'));
const ConversationPage = lazy(() => import('./features/chat/pages/ConversationPage'));
const AdminDashboardPage = lazy(() => import('./features/admin/pages/AdminDashboardPage'));
const BlogListPage = lazy(() => import('./features/blog/pages/BlogListPage'));
const BlogPostPage = lazy(() => import('./features/blog/pages/BlogPostPage'));

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes (matches backend cache)
    },
  },
});

/**
 * AppContent Component
 * Contains all routes and uses auth context
 */
const AppContent = () => {
  const { user, logout, isAuthenticated, isWerkgever, isStudent, isAdmin, isLoading } = useAuthContext();

  // Auto-recover from stale bundles after a deploy (reloads once per session).
  useAppVersionGuard();

  if (isLoading) {
    return <WebsiteLoadingScreen />;
  }

  // Employers who have not yet accepted the platform agreement are sent back to
  // /dashboard (where the onboarding/agreement flow is shown), so they cannot
  // bypass it by navigating to another employer route directly. Students/admins
  // are unaffected.
  const employerMustAcceptTerms = isWerkgever() && user?.hasAcceptedTerms !== true;
  const gateEmployer = (element) =>
    employerMustAcceptTerms ? <Navigate to="/dashboard" replace /> : element;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Scroll Position Manager */}
      <ScrollManager />

      {/* Header Navigation */}
      <Header gebruiker={user} onLogout={logout} />

      {/* Main Content with padding for fixed header */}
      <div className="pt-16">
        <Suspense fallback={<WebsiteLoadingScreen />}>
        <Routes>
          {/* Authentication Routes - Redirect to dashboard if already logged in */}
          <Route
            path={ROUTES.LOGIN}
            element={
              isAuthenticated() ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <LoginPage />
              )
            }
          />
          <Route
            path={ROUTES.REGISTER}
            element={
              isAuthenticated() ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <RegisterPage />
              )
            }
          />
          <Route path={ROUTES.CONFIRM_ACCOUNT} element={<ConfirmAccountPage />} />
          <Route
            path={ROUTES.FORGOT_PASSWORD}
            element={
              isAuthenticated() ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <ForgotPasswordPage />
              )
            }
          />
          <Route
            path={ROUTES.RESET_PASSWORD}
            element={
              isAuthenticated() ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <ResetPasswordPage />
              )
            }
          />

          {/* Public Routes */}
          <Route path="/blogs" element={<BlogListPage />} />
          <Route path="/blogs/:slug" element={<BlogPostPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/finance-den-haag" element={<FinanceLandingPage />} />

          {/* Vacancy Display Routes - Public (students can view vacancies) */}
          <Route
            path="/vacancy/:id"
            element={<VacancyPage gebruiker={user} />}
          />
          <Route path="/search-results" element={<SearchResults />} />

          {/* Admin Dashboard Route */}
          <Route
            path="/admin/*"
            element={
              isAuthenticated() && isAdmin() ? (
                <AdminDashboardPage />
              ) : (
                <Navigate to={ROUTES.LOGIN} replace />
              )
            }
          />

          {/* Protected Routes - Dashboard */}
          <Route
            path="/dashboard/*"
            element={
              isAuthenticated() ? (
                isAdmin() ? (
                  <Navigate to={ROUTES.ADMIN_DASHBOARD} replace />
                ) : isWerkgever() ? (
                  <WerkgeverDashboardPage />
                ) : isStudent() ? (
                  <StudentDashboardPage />
                ) : (
                  <Navigate to={ROUTES.LOGIN} replace />
                )
              ) : (
                <Navigate to={ROUTES.LOGIN} replace />
              )
            }
          />
          <Route
            path="/dashboard"
            element={
              isAuthenticated() ? (
                isAdmin() ? (
                  <Navigate to={ROUTES.ADMIN_DASHBOARD} replace />
                ) : isWerkgever() ? (
                  <WerkgeverDashboardPage />
                ) : isStudent() ? (
                  <StudentDashboardPage />
                ) : (
                  <Navigate to={ROUTES.LOGIN} replace />
                )
              ) : (
                <Navigate to={ROUTES.LOGIN} replace />
              )
            }
          />
          {/* Protected Route - Plaats Vacature */}
          <Route
            path="/dashboard/plaats-vacature"
            element={
              isAuthenticated() && isWerkgever() ? (
                gateEmployer(<PlaatsVacaturePage />)
              ) : (
                <Navigate to={ROUTES.LOGIN} replace />
              )
            }
          />
          {/* Invoice request */}
          <Route
            path="/dashboard/factuur-aanvragen"
            element={
              isAuthenticated() && isWerkgever() ? (
                gateEmployer(<InvoiceRequestPage />)
              ) : (
                <Navigate to={ROUTES.LOGIN} replace />
              )
            }
          />
          {/* Cadeaukaart request */}
          <Route
            path="/cadeaukaart-aanvragen"
            element={
              isAuthenticated() && isStudent() ? (
                <CadeaukaartRequestPage gebruiker={user} />
              ) : (
                <Navigate to={ROUTES.LOGIN} replace />
              )
            }
          />
          {/* Protected Route - Bewerk Vacature */}
          <Route
            path="/dashboard/bewerk-vacature/:id"
            element={
              isAuthenticated() && isWerkgever() ? (
                gateEmployer(<PlaatsVacaturePage />)
              ) : (
                <Navigate to={ROUTES.LOGIN} replace />
              )
            }
          />
          <Route
            path="/dashboard/vacature-success"
            element={
              isAuthenticated() && isWerkgever() ? (
                gateEmployer(<VacatureSuccessPage />)
              ) : (
                <Navigate to={ROUTES.LOGIN} replace />
              )
            }
          />
          <Route
            path="/berichten"
            element={
              isAuthenticated() ? (
                gateEmployer(<ComingSoonPage />)
              ) : (
                <Navigate to={ROUTES.LOGIN} replace />
              )
            }
          />
          {/* Chat Routes */}
          <Route
            path="/chat"
            element={
              isAuthenticated() ? (
                gateEmployer(<ChatPage />)
              ) : (
                <Navigate to={ROUTES.LOGIN} replace />
              )
            }
          />
          <Route
            path="/chat/:conversationId"
            element={
              isAuthenticated() ? (
                gateEmployer(<ConversationPage />)
              ) : (
                <Navigate to={ROUTES.LOGIN} replace />
              )
            }
          />

          {/* Landing Pages */}
          <Route
            path="/gratis-vacature"
            element={
              <LandingPageGuard>
                <LandingPageGratisVacature />
              </LandingPageGuard>
            }
          />

          {/* Default Route - Homepage (conditionally shows a campaign landing page when active) */}
          <Route path="/" element={<HomepageRouter fallback={<Homepagina />} campaign={<ZorgLandingPage />} financeCampaign={<FinanceLandingPage />} />} />

          {/* 404 Route */}
          <Route path="*" element={<Navigate to={ROUTES.LOGIN} replace />} />
        </Routes>
        </Suspense>
      </div>

      {/* Footer */}
      <Footer />

      {/* Compliance Notice (Cookie/Terms) */}
      <ComplianceNotice />
    </div>
  );
};

/**
 * Main App Component
 * Wraps everything in QueryClientProvider, AuthProvider and Router
 */
const App = () => {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <Router>
            <AppContent />
          </Router>
        </AuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
